import React, { useState } from "react";
import AccountLayout from "../../account/AccountLayout";
import { useAccount } from "../../account/AccountProvider";
import { patchProfile } from "../../account/accountApiClient";
import { AccountButton, AccountCard, AccountStatus } from "../../account/AccountUi";
import { formatDate } from "../../account/entitlementAdapter";
import { captureScope } from "../../auth/authSession";

function methodLabel(id) {
  if (id === "google.com") return "Google";
  if (id === "apple.com") return "Apple (iPhone)";
  if (id === "password") return "Email (legacy)";
  return id || "Unknown";
}

export default function ProfileSecurityPage() {
  return (
    <AccountLayout
      title="Profile & security"
      description="Name, email, and how you sign in to Stratify."
      path="/account/profile"
    >
      <ProfileSecurityBody />
    </AccountLayout>
  );
}

function ProfileSecurityBody() {
  const { profile, profileState, profileError, reloadProfile } = useAccount();
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [tone, setTone] = useState("info");

  const displayName = editing ? name : profile?.displayName || "";
  const methods = profile?.signInMethods?.length ? profile.signInMethods : ["google.com"];

  async function onSave(event) {
    event.preventDefault();
    if (!profile) return;
    setBusy(true);
    setMessage(null);
    try {
      await patchProfile({
        displayName: name.trim(),
        expectedRevision: profile.revision,
        scope: captureScope(),
      });
      await reloadProfile();
      setEditing(false);
      setTone("info");
      setMessage("Display name updated.");
    } catch (err) {
      if (err?.code === "revision_conflict") {
        await reloadProfile();
        setTone("error");
        setMessage("This profile was updated elsewhere. Reload and try again.");
      } else {
        setTone("error");
        setMessage(err?.message || "Could not update profile.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
      <div className="space-y-5">
        {profileState === "loading" && !profile ? <AccountStatus>Loading profile…</AccountStatus> : null}
        {profileState === "error" ? (
          <AccountCard>
            <AccountStatus tone="error">{profileError?.message || "We couldn’t load your profile."}</AccountStatus>
            <AccountButton variant="secondary" className="mt-3" onClick={reloadProfile}>
              Try again
            </AccountButton>
          </AccountCard>
        ) : null}

        {profile ? (
          <>
            <AccountCard>
              <form onSubmit={onSave} className="space-y-4">
                <label className="block text-sm font-medium text-ink">
                  Display name
                  <input
                    value={editing ? name : displayName}
                    onChange={(event) => {
                      if (!editing) {
                        setEditing(true);
                        setName(profile.displayName || "");
                      }
                      setName(event.target.value);
                    }}
                    onFocus={() => {
                      if (!editing) {
                        setEditing(true);
                        setName(profile.displayName || "");
                      }
                    }}
                    maxLength={80}
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-sm text-ink"
                  />
                </label>
                <p className="text-sm text-muted">Email: {profile.email || "Not provided by Google"}</p>
                <p className="text-sm text-muted">
                  Email verified: {profile.emailVerified ? "Yes" : "No"}
                  {profile.createdAt ? ` · Created ${formatDate(profile.createdAt)}` : ""}
                </p>
                {message ? <AccountStatus tone={tone}>{message}</AccountStatus> : null}
                <AccountButton type="submit" disabled={busy || !editing}>
                  {busy ? "Saving…" : "Save name"}
                </AccountButton>
              </form>
            </AccountCard>

            <AccountCard>
              <h2 className="text-base font-semibold text-ink">Sign-in method</h2>
              <p className="mt-2 text-sm text-muted">
                Website sign-in is Google only. Recover access through your Google account, not a Stratify password.
              </p>
              <ul className="mt-3 space-y-1 text-sm text-ink/80">
                {methods.map((id) => (
                  <li key={id}>{methodLabel(id)}</li>
                ))}
              </ul>
              <a
                href="https://accounts.google.com/signin/recovery"
                className="mt-4 inline-flex min-h-11 items-center text-sm font-semibold text-ink underline underline-offset-4"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google account recovery
              </a>
            </AccountCard>
          </>
        ) : null}
      </div>
  );
}
